import { Trans, Select } from '@lingui/macro';
import classnames from 'classnames';
import React, { Fragment, FC, useState, useRef, ReactNode, useEffect, memo } from 'react';

import Image from 'common/Image/Image';

import { TLatestRequest, TProvider } from '../LatestRequests.helpers';
import './LatestRequest.scss';

const MAX_DETAILS_HEIGHT = 200;
const MAX_PROVIDERS_AVATARS = 5;

type Props = {
  shouldLoadAvatars: boolean;
} & TLatestRequest;

const LatestRequest: FC<Props> = (props) => {
  const [isExpandable, setIsExpandable] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const detailsElRef = useRef<HTMLDListElement>(null);

  useEffect(() => {
    if (detailsElRef.current && detailsElRef.current.offsetHeight > MAX_DETAILS_HEIGHT) setIsExpandable(true);
  }, []);

  const getProviderAvatarCN = (isLast: boolean): string =>
    classnames('latestRequest__providerAvatar', {
      latestRequest__providerAvatar_last: isLast,
    });

  const renderProviders = (providers: TProvider[]): ReactNode => {
    const isExtraProviders = providers.length > MAX_PROVIDERS_AVATARS;
    const items = isExtraProviders ? providers.slice(0, MAX_PROVIDERS_AVATARS - 1) : providers;

    return (
      <div className="latestRequest__providers">
        {items.map((provider, index, { length }) => (
          <Image
            src={provider.avatar}
            webp
            multiPixelRatios
            size="s"
            className={getProviderAvatarCN(index === length - 1)}
            alt=""
            key={provider.id}
          />
        ))}
        {isExtraProviders && <span className="latestRequest__providerAvatarExtra">+2</span>}
      </div>
    );
  };

  const { user, providers } = props;

  const latestRequestCN = classnames('latestRequest', {
    latestRequest_expandable: isExpandable,
    latestRequest_expanded: isExpanded,
  });

  let gender = '';
  if (user.gender === '1') gender = 'male';
  if (user.gender === '2') gender = 'female';

  return (
    <div className={latestRequestCN}>
      <div className="latestRequest__user">
        {user.avatar && props.shouldLoadAvatars && (
          <Image src={user.avatar} webp multiPixelRatios size="s" alt="" className="latestRequest__userAvatar" />
        )}
        <span className="latestRequest__userAdded">
          <Select
            value={gender}
            male={`${props.user.name} dodał zapytanie`}
            female={`${props.user.name} dodała zapytanie`}
            other=""
          />
          :
        </span>
      </div>

      <div className="latestRequest__detailsWrap">
        <dl className="latestRequest__details" ref={detailsElRef}>
          {props.details
            .filter((detail) => detail.answer)
            .map((detail) => (
              <Fragment key={detail.question_id}>
                <dt className="latestRequest__detailQuestion">{detail.question}</dt>
                <dd className="latestRequest__detailAnswer">{detail.answer}</dd>
              </Fragment>
            ))}
        </dl>
        {isExpandable && !isExpanded && (
          <button type="button" className="latestRequest__showMore" onClick={() => setIsExpanded(true)}>
            <Trans>Pokaż więcej</Trans>
          </button>
        )}
      </div>

      {providers.length > 0 && (
        <div className="latestRequest__providersSection">
          <p className="latestRequest__offers">
            <Trans>Oferty wykonawców</Trans>: {providers.length}
          </p>
          {props.shouldLoadAvatars && renderProviders(providers)}
        </div>
      )}
    </div>
  );
};

export default memo(LatestRequest);
